import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './hooks/useAuth';
import { Layout } from './components/Layout';
import { LoginPage } from './components/LoginPage';
import { AuthCallback } from './components/AuthCallback';
import { SharePointOAuthCallback } from './components/SharePointOAuthCallback';
import { ReviewDashboard } from './components/ReviewDashboard';
import { ContractWizard } from './components/ContractWizard';
import { EditContractWizard } from './components/EditContractWizard';
import { ContractDetail } from './components/ContractDetail';
import { ClientManager, ClientDetail } from './components/ClientManager';
import { TemplateManager, ContractTemplates, ScopesManager } from './components/TemplateManager';
import { ConnectionsManager } from './components/ConnectionsManager';
import { Settings, Users, Profile } from './components/Settings';
import { ClauseManager } from './components/ClauseManager';
import { AiScopeAssist } from './components/AiScopeAssist';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/settings/connections/sharepoint/callback" element={
            <PrivateRoute>
              <SharePointOAuthCallback />
            </PrivateRoute>
          } />
          
          <Route
            path="/"
            element={
              <PrivateRoute>
                <Layout />
              </PrivateRoute>
            }
          >
          <Route index element={<Navigate to="/contracts" replace />} />
          <Route path="contracts" element={<ReviewDashboard />} />
          <Route path="contracts/new" element={<ContractWizard />} />
          <Route path="contracts/:id/edit" element={<EditContractWizard />} />
          <Route path="contracts/:id" element={<ContractDetail />} />
          <Route path="clients" element={<ClientManager />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="templates" element={<TemplateManager />}>
            <Route index element={<Navigate to="/templates/contracts" replace />} />
            <Route path="contracts" element={<ContractTemplates />} />
            <Route path="scopes" element={<ScopesManager />} />
          </Route>
          <Route path="settings" element={<Settings />}>
            <Route index element={<Navigate to="/settings/profile" replace />} />
            <Route path="profile" element={<Profile />} />
            <Route path="users" element={<Users />} />
            <Route path="connections" element={<ConnectionsManager />} />
          </Route>
          <Route path="clauses" element={<ClauseManager />} />
          <Route path="ai-assist" element={<AiScopeAssist />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />
    </>
  );
}

export default App;

